import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { ViewList as ViewListIcon, ViewModule as ViewModuleIcon } from '@mui/icons-material';
import {
  ListPageScaffold,
  ListPageMain,
  ListPageFiltersSection,
} from '../../components/common/list-page';
import InventoryStats from '../../components/inventory/InventoryStats';
import InventoryQueryFilters from '../../components/inventory/InventoryQueryFilters';
import { CategoriesService } from '../../api/services/CategoriesService';
import { TagsService } from '../../api/services/TagsService';
import { MaterialsService } from '../../api/services/MaterialsService';
import { ColorsService } from '../../api/services/ColorsService';
import { Category } from '../../api/models/Category';
import { Tag } from '../../api/models/Tag';
import {
  InventoryItem,
  PaginationInfo,
  InventoryFilterState,
  DEFAULT_BRANCH_INVENTORY_SORT,
  WarehouseInventorySortField,
} from '../../types/inventory';
import { WarehouseInventoryProductsService } from '../../api/services/WarehouseInventoryProductsService';
import { UpdateAvailabilityStatusDto } from '../../api/models/UpdateAvailabilityStatusDto';
import { buildWarehouseInventoryFindAllArgs } from '../../utils/inventory/warehouseInventoryListQuery';
import { mapWarehouseInventoryProductsToInventoryItems } from '../../utils/inventory/mapWarehouseInventoryProductToInventoryItem';
import { WarehouseInventorySummaryDto } from '../../api/models/WarehouseInventorySummaryDto';
import { LocationsService } from '../../api/services/LocationsService';
import { Location } from '../../api/models/Location';
import InventoryDetailModal from '../../components/inventory/InventoryDetailModal';
import InventoryDrawer from '../../components/inventory/InventoryDrawer';
import { BranchTable, InventoryGrid } from '../../components/inventory/InventoryQueryTable';
import InventoryTransferModal from '../../components/inventory/InventoryTransferModal/InventoryTransferModal';
import {
  ReserveConfirmationModal,
  UnreserveConfirmationModal,
} from '../../components/modals/confirmation';
import CommonPagination from '../../components/common/Pagination';
import EmptyStateMessage from '../../components/common/EmptyStateMessage';
import { printInventoryUnitLabel } from '../../utils/printInventoryLabels';
import { INVENTORY_MESSAGES } from '../../utils/constants';
import {
  INV_AVAILABILITY_BLOCKED_LABEL,
  INV_AVAILABILITY_WRITE_OFF_LABEL,
  isAvailabilityWrittenOff,
} from '../../constants/inventory-status-labels';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../redux/slices/auth.slice';
import { useInventoryBranchPermissions } from '../../hooks/useInventoryBranchPermissions';
import { useCanChangeInventoryLocation } from '../../hooks/useCanChangeInventoryLocation';
import { useInventoryTransferScan } from '../../hooks/useInventoryTransferScan';

const InventoryBranchQueryPage: React.FC = () => {
  const branchPerms = useInventoryBranchPermissions();
  const currentUser = useSelector(selectCurrentUser) as {
    primaryLocationId?: number | null;
  } | null;
  const canChangeInventoryLocation = useCanChangeInventoryLocation();
  /** Acotado a sucursal asignada salvo admin o flag canSellFromOtherLocations (mismo criterio que el API). */
  const isLocationLocked = Boolean(currentUser?.primaryLocationId) && !canChangeInventoryLocation;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [inventoryStats, setInventoryStats] = useState<WarehouseInventorySummaryDto>({
    totalProducts: 0,
    totalValue: 0,
    totalCostValue: 0,
    writeOffCount: 0,
    statusSummary: [],
    availabilityStatusSummary: [],
    mostCommonCondition: '',
    lastActivityDate: '',
  });
  const [isStatsLoading, setIsStatsLoading] = useState<boolean>(true);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLocationsLoading, setIsLocationsLoading] = useState<boolean>(true);

  // Dynamic data for filters
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [tagsList, setTagsList] = useState<Tag[]>([]);
  const [materials, setMaterials] = useState<{ id: number; name: string }[]>([]);
  const [colorsCatalog, setColorsCatalog] = useState<{ id: number; name: string }[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isMetadataLoading, setIsMetadataLoading] = useState<boolean>(true);

  // Using the new filter state format with default branch filter
  const [filters, setFilters] = useState<InventoryFilterState>({
    searchTerm: '',
    createdAtStart: '',
    createdAtEnd: '',
    status: '',
    locationId:
      isLocationLocked && currentUser?.primaryLocationId
        ? String(currentUser.primaryLocationId)
        : '',
    categoryName: '',
    recordCode: '',
    searchTag: '',
    materialId: '',
    diameterSize: '',
    color: '',
    workshopCode: '',
    weightGramsMin: undefined,
    weightGramsMax: undefined,
    salePriceMin: undefined,
    salePriceMax: undefined,
    salePricePerGramMin: undefined,
    salePricePerGramMax: undefined,
  });

  // Mantener alcance en sucursal primaria cuando está bloqueado
  useEffect(() => {
    if (!isLocationLocked || !currentUser?.primaryLocationId) return;
    const primaryId = String(currentUser.primaryLocationId);
    setFilters((prev) =>
      prev.locationId === primaryId ? prev : { ...prev, locationId: primaryId },
    );
  }, [isLocationLocked, currentUser?.primaryLocationId]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [inventorySort, setInventorySort] = useState<{
    sortBy: WarehouseInventorySortField;
    sortOrder: 'asc' | 'desc';
  }>(DEFAULT_BRANCH_INVENTORY_SORT);

  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isReserveModalOpen, setIsReserveModalOpen] = useState<boolean>(false);
  const [isUnreserveModalOpen, setIsUnreserveModalOpen] = useState<boolean>(false);
  const [itemToReserve, setItemToReserve] = useState<InventoryItem | null>(null);
  const [isReserving, setIsReserving] = useState<boolean>(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Estado para el modal de transferencia
  const [isTransferModalOpen, setIsTransferModalOpen] = useState<boolean>(false);
  const [selectedItemsDetails, setSelectedItemsDetails] = useState<InventoryItem[]>([]);

  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');

  // Add a search trigger state to control when searches are executed
  const [searchTrigger, setSearchTrigger] = useState(0);

  // Fetch locations from API
  const fetchLocations = useCallback(async () => {
    setIsLocationsLoading(true);
    try {
      const response = await LocationsService.locationsControllerFindAll();

      // Process the response data similar to other components
      let locationsList: Location[] = [];

      if (response && Array.isArray(response.data)) {
        locationsList = response.data as Location[];
      } else if (response && Array.isArray(response)) {
        // Handle case where response is directly an array
        locationsList = response as Location[];
      }

      setLocations(locationsList);

      const primaryLocationId = currentUser?.primaryLocationId ?? null;

      if (primaryLocationId && isLocationLocked) {
        const assignedLocation = locationsList.find(
          (location) => location.id === primaryLocationId,
        );

        if (assignedLocation) {
          setFilters((prev) =>
            prev.locationId === assignedLocation.id.toString()
              ? prev
              : { ...prev, locationId: assignedLocation.id.toString() },
          );
        }
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    } finally {
      setIsLocationsLoading(false);
    }
  }, [filters.locationId, currentUser?.primaryLocationId, isLocationLocked]);

  // Fetch metadata for filters
  const fetchMetadata = useCallback(async () => {
    setIsMetadataLoading(true);
    try {
      // Fetch categories, tags, and materials in parallel
      const [categoriesResponse, tagsResponse, materialsResponse, colorsResponse] =
        await Promise.all([
          CategoriesService.categoriesControllerFindAll(1, 1000),
          TagsService.tagsControllerFindAll(1, 1000),
          MaterialsService.materialsControllerFindAll(1, 1000),
          ColorsService.colorsControllerFindAll(1, 1000),
        ]);

      // Process categories
      let categoriesList: Category[] = [];
      if (categoriesResponse && Array.isArray(categoriesResponse.data)) {
        categoriesList = categoriesResponse.data as Category[];
      } else if (categoriesResponse && Array.isArray(categoriesResponse)) {
        categoriesList = categoriesResponse as Category[];
      }
      const productTypesList = categoriesList.map((cat) => cat.name);
      setProductTypes(productTypesList);

      // Process tags
      let tagsList: Tag[] = [];
      if (tagsResponse && Array.isArray(tagsResponse.data)) {
        tagsList = tagsResponse.data as Tag[];
      } else if (tagsResponse && Array.isArray(tagsResponse)) {
        tagsList = tagsResponse as Tag[];
      }
      const tagsListFormatted = ['Todas', ...tagsList.map((tag) => tag.name)];
      setTags(tagsListFormatted);
      setTagsList(tagsList);

      // Process materials
      const materialsList = Array.isArray(materialsResponse?.data)
        ? materialsResponse.data
        : Array.isArray(materialsResponse)
          ? materialsResponse
          : [];
      setMaterials(
        materialsList.map((material: any) => ({
          id: material.id,
          name: material.name,
        })),
      );

      const colorsList = Array.isArray(colorsResponse?.data)
        ? colorsResponse.data
        : Array.isArray(colorsResponse)
          ? colorsResponse
          : [];
      setColorsCatalog(
        colorsList.map((c: any) => ({
          id: c.id,
          name: c.name,
        })),
      );
    } catch (error) {
      console.error('Error fetching metadata:', error);
      // Fallback to empty arrays
      setProductTypes([]);
      setTags(['Todas']);
      setMaterials([]);
      setColorsCatalog([]);
    } finally {
      setIsMetadataLoading(false);
    }
  }, []);

  // Fetch inventory stats from API
  const fetchInventoryStats = useCallback(async (currentFilters: InventoryFilterState) => {
    setIsStatsLoading(true);
    try {
      // Allow all locations when no specific branch is selected
      const response =
        await WarehouseInventoryProductsService.warehouseInventoryProductsControllerGetSummary(
          currentFilters.locationId && currentFilters.locationId !== '0'
            ? Number(currentFilters.locationId)
            : undefined, // locationId (number) - Treat "0" as "no filter"
          undefined, // productCode
          undefined, // status - not used for branch queries
          undefined, // transferredToBranch
          undefined, // productCodeUnique (removed)
          undefined, // productName (removed)
          currentFilters.searchTerm || undefined, // searchTerm (new)
          currentFilters.categoryName || undefined, // categoryName (Tipo de producto)
          currentFilters.createdAtStart || undefined,
          currentFilters.createdAtEnd || undefined,
          currentFilters.recordCode || undefined, // recordCode (Número de registro)
          currentFilters.status && currentFilters.status !== 'ALL'
            ? currentFilters.status
            : undefined, // availabilityStatus (Disponible, Vendido, …)
          currentFilters.searchTag || undefined, // searchTag (Etiqueta)
          currentFilters.materialId ? Number(currentFilters.materialId) : undefined, // materialId
          currentFilters.diameterSize || undefined, // diameterSize
          currentFilters.color || undefined, // color
          currentFilters.workshopCode || undefined, // workshopCode
          currentFilters.weightGramsMin,
          currentFilters.weightGramsMax,
          currentFilters.salePriceMin,
          currentFilters.salePriceMax,
          currentFilters.salePricePerGramMin,
          currentFilters.salePricePerGramMax,
          undefined, // excludeCancelledAvailability
          undefined, // warehousePlacementOnly
          true, // assignedPlacementOnly — solo piezas ya en sucursal (ASSIGNED)
        );
      setInventoryStats(response);
    } catch (error) {
      console.error('Error fetching inventory stats:', error);
    } finally {
      setIsStatsLoading(false);
    }
  }, []);

  // Fetch data from API using the new filter format
  const fetchInventoryData = useCallback(
    async (currentFilters: InventoryFilterState) => {
      setIsLoading(true);

      try {
        const response =
          await WarehouseInventoryProductsService.warehouseInventoryProductsControllerFindAll(
            ...buildWarehouseInventoryFindAllArgs({
              page: currentPage,
              limit: itemsPerPage,
              filters: currentFilters,
              availabilityStatus:
                currentFilters.status && currentFilters.status !== 'ALL'
                  ? currentFilters.status
                  : undefined,
              sortBy: inventorySort.sortBy as string,
              sortOrder: inventorySort.sortOrder,
              scope: { assignedPlacementOnly: true },
              includeNumericFilters: true,
            }),
          );

        setInventory(mapWarehouseInventoryProductsToInventoryItems(response.data));
        setTotalItems(response.meta?.total || 0);
      } catch (error) {
        console.error('Error fetching inventory data:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage, itemsPerPage, inventorySort.sortBy, inventorySort.sortOrder],
  );

  const handleInventorySortChange = useCallback((field: WarehouseInventorySortField) => {
    setInventorySort((prev) => {
      if (prev.sortBy === field) {
        return {
          sortBy: field,
          sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
        };
      }
      return { sortBy: field, sortOrder: 'asc' };
    });
    setCurrentPage(1);
  }, []);

  // Store current filters in a ref to access in useEffect
  const currentFiltersRef = useRef(filters);
  useEffect(() => {
    currentFiltersRef.current = filters;
  }, [filters]);

  // Load initial data
  useEffect(() => {
    fetchInventoryData(currentFiltersRef.current);
    fetchInventoryStats(currentFiltersRef.current);
  }, [fetchInventoryData, fetchInventoryStats, searchTrigger]);

  // Load locations and metadata when component mounts
  useEffect(() => {
    fetchLocations();
    fetchMetadata();
  }, [fetchLocations, fetchMetadata]);

  // Initial search trigger
  useEffect(() => {
    setSearchTrigger(1);
  }, []);

  // Paginación
  const paginationInfo: PaginationInfo = useMemo(() => {
    return {
      currentPage,
      totalPages: Math.ceil(totalItems / itemsPerPage),
      itemsPerPage,
      totalItems,
    };
  }, [currentPage, itemsPerPage, totalItems]);

  // Update to handle the new filter format
  const handleFilterChange = useCallback((newFilters: InventoryFilterState) => {
    // Removed branch restriction temporarily
    const filtersWithoutBranch = {
      ...newFilters,
      // locationId: mockUserBranch.id.toString(), // Removed branch restriction temporarily
      // For branch query, we use status field directly
      status: newFilters.status === 'ALL' ? '' : newFilters.status, // Map status for API compatibility, treat "ALL" as empty
    };
    setFilters(filtersWithoutBranch);
    setCurrentPage(1);
    setSearchTrigger((prev) => prev + 1);
  }, []);

  // Manejar cambio de página
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Manejar cambio en items por página
  const handleItemsPerPageChange = useCallback((newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  // Manejar click en ver detalles
  const handleViewDetails = (id: string) => {
    const item = inventory.find((item) => item.id === id);
    if (item) {
      setSelectedItem(item);
      setIsDetailModalOpen(true);
    }
  };

  // Manejar click en editar: abre el modal de detalle (ahí se puede editar si tiene permiso allowEditInBranch)
  const handleEditItem = (id: string) => {
    const item = inventory.find((i) => i.id === id);
    if (item) {
      setSelectedItem(item);
      setIsDetailModalOpen(true);
    }
  };

  // Manejar guardar cambios desde el drawer (por si se usa en el futuro)
  const handleSaveItem = async (_updatedItem?: unknown) => {
    setSearchTrigger((prev) => prev + 1);
    setIsDrawerOpen(false);
  };

  // Handle reserve/unreserve action
  const handleReserveItem = (id: string) => {
    const item = inventory.find((item) => item.id === id);
    if (item) {
      setItemToReserve(item);
      if (item.availabilityStatus === 'Disponible') {
        setIsReserveModalOpen(true);
      } else if (item.availabilityStatus === INV_AVAILABILITY_BLOCKED_LABEL) {
        setIsUnreserveModalOpen(true);
      }
    }
  };

  // Handle reserve confirmation
  const handleConfirmReserve = async (comment?: string) => {
    if (!itemToReserve?.originalId) return;

    setIsReserving(true);
    try {
      await WarehouseInventoryProductsService.warehouseInventoryProductsControllerUpdateAvailabilityStatus(
        itemToReserve.originalId,
        {
          status: UpdateAvailabilityStatusDto.status.RESERVED,
          ...(comment ? { comment } : {}),
        },
      );

      setIsReserveModalOpen(false);
      setItemToReserve(null);

      // Refresh data
      setSearchTrigger((prev) => prev + 1);
    } catch (error: any) {
      console.error('Error reserving product:', error);
      setNotification({
        open: true,
        ...INVENTORY_MESSAGES.RESERVE_ERROR(error?.body?.message),
      });
    } finally {
      setIsReserving(false);
    }
  };

  // Handle unreserve confirmation
  const handleConfirmUnreserve = async (comment?: string) => {
    if (!itemToReserve?.originalId) return;

    setIsReserving(true);
    try {
      await WarehouseInventoryProductsService.warehouseInventoryProductsControllerUpdateAvailabilityStatus(
        itemToReserve.originalId,
        {
          status: UpdateAvailabilityStatusDto.status.AVAILABLE,
          ...(comment ? { comment } : {}),
        },
      );

      setIsUnreserveModalOpen(false);
      setItemToReserve(null);

      // Refresh data
      setSearchTrigger((prev) => prev + 1);
    } catch (error: any) {
      console.error('Error unreserving product:', error);
      setNotification({
        open: true,
        ...INVENTORY_MESSAGES.RELEASE_RESERVE_ERROR(error?.body?.message),
      });
    } finally {
      setIsReserving(false);
    }
  };

  // Handle notification close
  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  // Actualizar los detalles del producto cuando cambia el inventario o las selecciones
  useEffect(() => {
    // Cuando cambia el inventario, actualizamos los detalles de los productos seleccionados
    const newDetails = [...selectedItemsDetails];
    let changed = false;

    // Agregar nuevos elementos seleccionados que no estén en los detalles
    inventory.forEach((item) => {
      if (selectedItems.includes(item.id)) {
        // Verificar si este item ya está en los detalles
        const existingIndex = newDetails.findIndex((detail) => detail.id === item.id);
        if (existingIndex === -1) {
          // Si no existe, lo agregamos
          newDetails.push(item);
          changed = true;
        }
      }
    });

    // Solo actualizar el estado si hubo cambios
    if (changed) {
      setSelectedItemsDetails(newDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory, selectedItems]);

  // Manejar selección de elementos para transferencia
  const handleSelectItems = useCallback((selectedIds: string[]) => {
    setSelectedItems(selectedIds);

    // Si hay elementos deseleccionados, los eliminamos de los detalles
    setSelectedItemsDetails((prev) => prev.filter((item) => selectedIds.includes(item.id)));
  }, []);

  const handleSelectScannedItem = useCallback((item: InventoryItem) => {
    setSelectedItems((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]));
    setSelectedItemsDetails((prev) =>
      prev.some((detail) => detail.id === item.id) ? prev : [...prev, item],
    );
  }, []);

  const handleScanNotify = useCallback(
    (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
      setNotification({ open: true, message, severity });
    },
    [],
  );

  const { handleScanSubmit } = useInventoryTransferScan({
    context: 'branch',
    getFilters: () => currentFiltersRef.current,
    selectedItems,
    onSelectItem: handleSelectScannedItem,
    onNotify: handleScanNotify,
    enabled: branchPerms.canTransfer,
  });

  // Manejar la apertura del modal de transferencia
  const handleOpenTransferModal = useCallback(() => {
    if (selectedItems.length > 0) {
      setIsTransferModalOpen(true);
    }
  }, [selectedItems]);

  // Manejar el cierre del modal de transferencia
  const handleCloseTransferModal = useCallback(() => {
    setIsTransferModalOpen(false);
  }, []);

  // Manejar el éxito de la transferencia
  const handleTransferSuccess = useCallback(() => {
    setSelectedItems([]);
    setSelectedItemsDetails([]);
    setIsTransferModalOpen(false);
    fetchInventoryData(currentFiltersRef.current);
    fetchInventoryStats(currentFiltersRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manejar remover producto del modal
  const handleRemoveProductFromModal = useCallback((productId: string) => {
    setSelectedItems((prev) => prev.filter((id) => id !== productId));
    setSelectedItemsDetails((prev) => prev.filter((item) => item.id !== productId));
  }, []);

  // Convert string arrays to object arrays for the filter component
  const formattedCategories = useMemo(() => {
    return productTypes.map((tipo, index) => ({
      id: index + 1,
      name: tipo,
    }));
  }, [productTypes]);

  const formattedLocations = useMemo(() => {
    const branchLocations = locations
      .filter((location) => location.type === Location.type.BRANCH)
      .map((location) => ({ id: location.id, name: location.name }));

    if (isLocationsLoading) {
      return [];
    }

    return branchLocations;
  }, [locations, isLocationsLoading]);

  const handleLocationScopeChange = useCallback(
    (payload: { locationId: string; locationName: string }) => {
      setNotification({
        open: true,
        message: `Mostrando inventario de ${payload.locationName}`,
        severity: 'info',
      });
    },
    [],
  );
  const formattedItemTypes = useMemo(() => {
    return productTypes.map((tipo, index) => ({
      id: index + 1,
      name: tipo,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedItemMaterials = useMemo(() => {
    return tags.map((etiqueta, index) => ({
      id: index + 1,
      name: etiqueta,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedTags = useMemo(() => {
    return tagsList.map((tag) => ({
      id: tag.id,
      name: tag.name,
    }));
  }, [tagsList]);

  const formattedItemStatuses = useMemo(() => {
    return [
      { id: 'ALL', name: 'Todas los Estados' },
      { id: 'AVAILABLE', name: 'Disponible' },
      { id: 'SOLD', name: 'Vendido' },
      { id: 'RESERVED', name: INV_AVAILABILITY_BLOCKED_LABEL },
      { id: 'PARTIAL_PAYMENT', name: 'Pago Pendiente' },
      { id: 'CANCELLED', name: INV_AVAILABILITY_WRITE_OFF_LABEL },
    ];
  }, []);

  return (
    <ListPageScaffold>
      <ListPageMain sectionGap={1}>
        {/* Dashboard Informativo */}
        {isStatsLoading ? (
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 1,
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '120px',
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            <InventoryStats
              totalProducts={inventoryStats.totalProducts}
              totalValue={inventoryStats.totalValue}
              totalCostValue={inventoryStats.totalCostValue}
              writeOffCount={inventoryStats.writeOffCount ?? 0}
              lastActivityDate={inventoryStats.lastActivityDate}
              variant="branch"
              kpiPermissionProfile="branch"
              availabilityStatusSummary={inventoryStats.availabilityStatusSummary}
              compactSpacing
            />
          </Box>
        )}

        <ListPageFiltersSection>
          <InventoryQueryFilters
            categories={formattedCategories}
            locations={formattedLocations}
            statuses={formattedItemStatuses}
            tags={formattedTags}
            colors={colorsCatalog}
            materials={materials}
            onFilterChange={handleFilterChange}
            initialFilters={filters}
            isInventoryBranchQuery={true}
            selectedItems={selectedItems}
            onTransferClick={handleOpenTransferModal}
            onScanSubmit={branchPerms.canTransfer ? handleScanSubmit : undefined}
            isLocationLocked={isLocationLocked}
            onLocationScopeChange={handleLocationScopeChange}
            showPieceNumericFilters
            hideSecondaryFilterPlaceholders
            rightControls={
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode: 'table' | 'grid' | null) =>
                  newMode != null && setViewMode(newMode)
                }
                aria-label="vista de inventario"
                size="small"
                sx={(theme) => ({
                  '& .MuiToggleButton-root': {
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.secondary,
                    '&.Mui-selected': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(6, 182, 212, 0.2)'
                          : 'rgba(6, 182, 212, 0.12)',
                      color: theme.palette.mode === 'dark' ? theme.palette.info.light : '#0891b2',
                      borderColor: theme.palette.info.main,
                      '&:hover': {
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(6, 182, 212, 0.28)'
                            : 'rgba(6, 182, 212, 0.18)',
                      },
                    },
                  },
                })}
              >
                <ToggleButton value="table" aria-label="vista tabla">
                  <Tooltip title="Vista tabla" arrow>
                    <ViewListIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="grid" aria-label="vista cuadrícula">
                  <Tooltip title="Vista cuadrícula" arrow>
                    <ViewModuleIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            }
          />
        </ListPageFiltersSection>

        {/* Loading indicator */}
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
              minHeight: '200px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Tabla / cuadrícula (mismo contenedor que almacén) */}
            {inventory.length > 0 && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 0,
                  border: 'none',
                  bgcolor: 'transparent',
                  overflow: 'hidden',
                  minHeight: 0,
                  px: 0,
                }}
              >
                {viewMode === 'table' ? (
                  <BranchTable
                    items={inventory}
                    onViewDetails={handleViewDetails}
                    pagination={paginationInfo}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    selectedItems={selectedItems}
                    onSelectItems={handleSelectItems}
                    onEditItem={handleEditItem}
                    onReserveItem={handleReserveItem}
                    onTransferSelected={handleOpenTransferModal}
                    hidePagination={true}
                    onPrintLabel={printInventoryUnitLabel}
                    sortBy={inventorySort.sortBy}
                    sortOrder={inventorySort.sortOrder}
                    onSortChange={handleInventorySortChange}
                  />
                ) : (
                  <InventoryGrid
                    variant="branch"
                    items={inventory}
                    onViewDetails={handleViewDetails}
                    selectedItems={selectedItems}
                    onSelectItems={handleSelectItems}
                    onTransferSelected={handleOpenTransferModal}
                    onEditItem={handleEditItem}
                    onReserveItem={handleReserveItem}
                    onPrintLabel={printInventoryUnitLabel}
                  />
                )}
              </Box>
            )}

            {/* Empty state */}
            {inventory.length === 0 && !isLoading && (
              <EmptyStateMessage message="No se encontraron piezas en inventario" minHeight={200} />
            )}

            {/* Paginación uniforme - Opción 4: Sin Paper, sin borde superior */}
            {inventory.length > 0 && (
              <Box>
                <CommonPagination
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={(event) => {
                    const newItemsPerPage = parseInt(event.target.value as string);
                    handleItemsPerPageChange(newItemsPerPage);
                  }}
                />
              </Box>
            )}
          </>
        )}
      </ListPageMain>

      {/* Modal de detalle */}
      <InventoryDetailModal
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        item={selectedItem}
        onEditItem={handleEditItem}
        allowEditInBranch={
          branchPerms.canEdit && !isAvailabilityWrittenOff(selectedItem?.availabilityStatus)
        }
        onSave={() => {
          fetchInventoryData(currentFiltersRef.current);
          fetchInventoryStats(currentFiltersRef.current);
        }}
        onItemUpdate={(updatedItem) => {
          setSelectedItem(updatedItem);
          setInventory((prevInventory) =>
            prevInventory.map((item) => (item.id === updatedItem.id ? updatedItem : item)),
          );
        }}
        blockHistoryRefreshKey={searchTrigger}
      />

      {/* Drawer de edición */}
      <InventoryDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={handleSaveItem}
        itemTypes={formattedItemTypes}
        itemMaterials={formattedItemMaterials}
        branches={formattedLocations}
        itemStatuses={formattedItemStatuses}
        editItem={selectedItem}
      />

      {/* Reserve Confirmation Modal */}
      <ReserveConfirmationModal
        open={isReserveModalOpen}
        onClose={() => setIsReserveModalOpen(false)}
        onConfirm={handleConfirmReserve}
        itemName={itemToReserve?.name}
        itemCode={itemToReserve?.id}
        isLoading={isReserving}
      />

      {/* Unreserve Confirmation Modal */}
      <UnreserveConfirmationModal
        open={isUnreserveModalOpen}
        onClose={() => setIsUnreserveModalOpen(false)}
        onConfirm={handleConfirmUnreserve}
        itemName={itemToReserve?.name}
        itemCode={itemToReserve?.id}
        isLoading={isReserving}
      />

      {/* Modal de transferencia */}
      <InventoryTransferModal
        open={isTransferModalOpen}
        onClose={handleCloseTransferModal}
        selectedProductIds={selectedItems}
        inventoryItems={selectedItemsDetails}
        onSuccess={handleTransferSuccess}
        locations={locations.map((loc) => ({
          id: loc.id,
          name: loc.name,
          type: loc.type,
        }))}
        onRemoveProduct={handleRemoveProductFromModal}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.severity === 'info' ? 3200 : 6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(6, 182, 212, 0.25)',
            ...(notification.severity === 'info'
              ? {
                  background: 'linear-gradient(to right, #06b6d4, #10b981)',
                  color: '#fff',
                  '& .MuiAlert-icon': { color: '#fff' },
                }
              : {}),
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </ListPageScaffold>
  );
};

export default InventoryBranchQueryPage;
